import { z } from 'zod';
import { solarDesignSchema } from './design.js';

/** DP.1.2 target boundary. Not wired to v1 APIs or a replacement for solarDesignSchema. */
const strict = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict();
export const entityIdSchema = z.string().regex(/^[1-9]\d*$/);
export const revisionSchema = entityIdSchema;
export const opaqueIdSchema = z.string().min(1).max(200).refine((value) => value.trim() === value && !/[\u0000-\u001f]/.test(value));
const timestamp = z.string().datetime({ offset: true });
const decimal = z.string().regex(/^(0|[1-9]\d*)(\.\d+)?$/);
export const quantitySchema = decimal.refine((value) => /[1-9]/.test(value), 'Quantity must be positive');
const currency = z.string().regex(/^[A-Z]{3}$/);
const finite = z.number().finite();

/** Host account/me returns JSON null for a guest and this bounded shopper profile otherwise. */
export const storefrontAccountProfileSchema = strict({
  email: z.string().email().max(320),
  firstName: z.string().max(200).nullable(),
  lastName: z.string().max(200).nullable(),
  trade: z.record(z.string(), z.unknown()).nullable().optional(),
  installer: z.record(z.string(), z.unknown()).nullable().optional(),
});

/** Major currency units, like commerce MoneyDto. Keep unit precision; no implicit pence conversion. */
export const moneySchema = strict({ amount: decimal, currency, taxInclusive: z.boolean() });
export const priceObservationSchema = z.discriminatedUnion('state', [
  strict({ state: z.literal('PRICED'), unit: moneySchema, pricedAt: timestamp }),
  strict({ state: z.literal('UNPRICED'), reason: z.enum(['MISSING_PRICE', 'NOT_SELLABLE', 'DESIGN_ONLY', 'CONTEXT_REQUIRED']) }),
]);

export const componentReferenceSchema = z.discriminatedUnion('kind', [
  strict({ kind: z.literal('CATALOGUE'), productId: entityIdSchema }),
  strict({ kind: z.literal('EXTERNAL'), componentId: opaqueIdSchema }),
]);

export const availabilityObservationSchema = strict({
  state: z.enum(['IN_STOCK', 'PARTIAL_STOCK', 'SUPPLIER_STOCK', 'SPECIAL_ORDER', 'OUT_OF_STOCK', 'UNKNOWN', 'DESIGN_ONLY']),
  fulfilment: z.enum(['OWN_STOCK', 'DROP_SHIP', 'EITHER', 'REFERRAL', 'UNKNOWN', 'NOT_SUPPLIED']),
  requestedQuantity: quantitySchema,
  ownAvailableQuantity: decimal.nullable(),
  supplierAvailableQuantity: decimal.nullable(),
  checkedAt: timestamp.nullable(),
  validUntil: timestamp.nullable(),
  leadTimeDays: strict({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() }).nullable(),
}).superRefine((value, ctx) => {
  if (value.validUntil && (!value.checkedAt || Date.parse(value.validUntil) <= Date.parse(value.checkedAt))) {
    ctx.addIssue({ code: 'custom', path: ['validUntil'], message: 'Availability expiry must follow its observation' });
  }
  if (value.leadTimeDays && value.leadTimeDays.min > value.leadTimeDays.max) {
    ctx.addIssue({ code: 'custom', path: ['leadTimeDays'], message: 'Invalid lead-time interval' });
  }
  if (value.state === 'DESIGN_ONLY' && value.fulfilment !== 'NOT_SUPPLIED') {
    ctx.addIssue({ code: 'custom', path: ['fulfilment'], message: 'Design-only equipment is not supplied' });
  }
  if (['IN_STOCK', 'PARTIAL_STOCK', 'SUPPLIER_STOCK'].includes(value.state)) {
    const quantity = value.state === 'SUPPLIER_STOCK' ? value.supplierAvailableQuantity : value.ownAvailableQuantity;
    if (!value.checkedAt || !value.validUntil || quantity === null || !/[1-9]/.test(quantity)) {
      ctx.addIssue({ code: 'custom', path: ['state'], message: 'A positive stock statement needs a dated quantity observation' });
    }
  }
});

export const catalogueItemV2Schema = strict({
  productId: entityIdSchema,
  ownCode: z.string().min(1).nullable(),
  mpn: z.string().min(1).nullable().optional(),
  title: z.string().min(1),
  role: z.enum(['panel', 'inverter', 'battery', 'mounting', 'extra']),
  brand: z.string().nullable(),
  productPath: z.string().regex(/^\/(?:p|kits)\/[^/?#\s]+$/).nullable().optional(),
  datasheetUrl: z.string().regex(/^(?:https?:\/\/[^\s]+|\/(?!\/)[^\s]*)$/).nullable().optional(),
  dimensionsMm: strict({ length: finite.positive(), width: finite.positive() }).nullable(),
  specifications: z.record(z.string(), strict({ value: z.union([finite, z.string(), z.boolean()]), unit: z.string().nullable() })),
  layoutEligibility: strict({ state: z.enum(['READY', 'MISSING_DATA', 'UNSUPPORTED']), missing: z.array(z.string().min(1)) }),
  price: priceObservationSchema,
  availability: availabilityObservationSchema,
}).superRefine((value, ctx) => {
  if (value.layoutEligibility.state === 'READY' && (value.layoutEligibility.missing.length > 0 || (value.role === 'panel' && !value.dimensionsMm))) {
    ctx.addIssue({ code: 'custom', path: ['layoutEligibility'], message: 'Ready panels need dimensions and no missing layout data' });
  }
});

export const cataloguePageV2Schema = strict({
  apiVersion: z.literal(2), items: z.array(catalogueItemV2Schema),
  total: z.number().int().nonnegative(), page: z.number().int().nonnegative(), size: z.number().int().min(1).max(96),
}).superRefine((value, ctx) => {
  const expected = Math.min(value.size, Math.max(0, value.total - value.page * value.size));
  if (value.items.length !== expected) ctx.addIssue({ code: 'custom', path: ['items'], message: 'Page must contain the filtered slice described by total/page/size' });
  if (new Set(value.items.map((item) => item.productId)).size !== value.items.length) {
    ctx.addIssue({ code: 'custom', path: ['items'], message: 'Duplicate product IDs' });
  }
});

/** Read-only server observation; price and stock inputs never come from the caller. */
export const commercialQuantitySchema = quantitySchema.refine((value) => {
  const [whole, fraction = ''] = value.split('.');
  return whole !== undefined && whole.length <= 4 && fraction.length <= 3 && Number(value) <= 9999;
}, 'Quantity must be at most 9999 with at most three decimal places');
export const commercialRequestV2Schema = strict({ lines: z.array(strict({
  lineId: opaqueIdSchema, component: componentReferenceSchema, description: z.string().min(1).max(500),
  quantity: commercialQuantitySchema,
})).min(1).max(96) }).superRefine((value, ctx) => {
  if (new Set(value.lines.map((line) => line.lineId)).size !== value.lines.length)
    ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Duplicate line IDs' });
});
export const commercialLineV2Schema = strict({
  lineId: opaqueIdSchema,
  component: componentReferenceSchema,
  description: z.string().min(1).max(500), quantity: commercialQuantitySchema,
  price: priceObservationSchema, lineAmount: moneySchema.nullable(),
  taxStatus: z.enum(['KNOWN', 'UNKNOWN']), availability: availabilityObservationSchema,
  shortfallQuantity: decimal.nullable(),
}).superRefine((value, ctx) => {
  if ((value.price.state === 'PRICED') !== (value.lineAmount !== null))
    ctx.addIssue({ code: 'custom', path: ['lineAmount'], message: 'A line amount requires a priced unit' });
  if (value.availability.requestedQuantity !== value.quantity)
    ctx.addIssue({ code: 'custom', path: ['availability'], message: 'Availability must describe requested quantity' });
});
export const commercialResponseV2Schema = strict({
  apiVersion: z.literal(2), lines: z.array(commercialLineV2Schema),
  pricedEquipmentSubtotals: z.array(strict({ currency, taxInclusive: z.boolean(), subtotal: decimal, pricedLineCount: z.number().int().positive() })),
  unpricedCount: z.number().int().nonnegative(),
}).superRefine((value, ctx) => {
  if (value.unpricedCount !== value.lines.filter((line) => line.price.state === 'UNPRICED').length)
    ctx.addIssue({ code: 'custom', path: ['unpricedCount'], message: 'Unpriced count does not match lines' });
});

export const designReferenceSchema = strict({ optionId: opaqueIdSchema, designId: opaqueIdSchema, revision: revisionSchema });
export const pinnedDesignReferenceSchema = designReferenceSchema.extend({
  projectId: opaqueIdSchema, documentSha256: z.string().regex(/^[a-f0-9]{64}$/),
});

export const projectManifestSchema = strict({
  schemaVersion: z.literal(1), name: z.string().min(1).max(200).refine((value) => value.trim() === value && !/^\s*$/.test(value)),
  sites: z.array(strict({ siteId: opaqueIdSchema, label: z.string().min(1).max(200), postcode: z.string().max(20), addressText: z.string().max(500).optional() })).max(100),
  options: z.array(strict({ optionId: opaqueIdSchema, siteId: opaqueIdSchema, designId: opaqueIdSchema, name: z.string().min(1).max(200) })).max(100),
  activeOptionId: opaqueIdSchema.nullable(),
}).superRefine((value, ctx) => {
  const sites = new Set(value.sites.map((site) => site.siteId));
  const options = new Set(value.options.map((option) => option.optionId));
  if (sites.size !== value.sites.length) ctx.addIssue({ code: 'custom', path: ['sites'], message: 'Duplicate site IDs' });
  if (options.size !== value.options.length || new Set(value.options.map((option) => option.designId)).size !== value.options.length) {
    ctx.addIssue({ code: 'custom', path: ['options'], message: 'Duplicate option or design association' });
  }
  if (value.options.some((option) => !sites.has(option.siteId))) ctx.addIssue({ code: 'custom', path: ['options'], message: 'Option references an unknown site' });
  if (value.activeOptionId !== null && !options.has(value.activeOptionId)) ctx.addIssue({ code: 'custom', path: ['activeOptionId'], message: 'Unknown active option' });
});

export const projectListRowSchema = strict({
  projectId: opaqueIdSchema, revision: revisionSchema, name: z.string().min(1).max(200), savedAt: timestamp,
  sites: z.array(strict({ siteId: opaqueIdSchema, label: z.string().min(1).max(200), postcode: z.string().max(20) })).max(100),
  optionCount: z.number().int().min(0).max(100), activeOptionId: opaqueIdSchema.nullable(),
  activeOptionName: z.string().min(1).max(200).nullable(),
}).superRefine((value, ctx) => {
  if ((value.activeOptionId === null) !== (value.activeOptionName === null))
    ctx.addIssue({ code: 'custom', path: ['activeOptionName'], message: 'Active option identity and name must agree' });
});
export const projectPageSchema = strict({
  apiVersion: z.literal(2), items: z.array(projectListRowSchema), total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(), size: z.number().int().min(1).max(96),
}).superRefine((value, ctx) => {
  const expected = Math.min(value.size, Math.max(0, value.total - value.page * value.size));
  if (value.items.length !== expected)
    ctx.addIssue({ code: 'custom', path: ['items'], message: 'Page does not match its filtered total' });
});
export const projectViewSchema = strict({
  apiVersion: z.literal(2), projectId: opaqueIdSchema, revision: revisionSchema, document: projectManifestSchema,
  options: z.array(strict({ optionId: opaqueIdSchema, siteId: opaqueIdSchema, designId: opaqueIdSchema,
    name: z.string().min(1).max(200), locked: z.boolean(), currentDesignRevision: revisionSchema.nullable() })).max(100),
}).superRefine((value, ctx) => {
  if (value.options.length !== value.document.options.length || value.options.some((option, index) => {
    const source = value.document.options[index];
    return !source || source.optionId !== option.optionId || source.siteId !== option.siteId ||
      source.designId !== option.designId || source.name !== option.name ||
      (option.locked && option.currentDesignRevision !== null);
  })) ctx.addIssue({ code: 'custom', path: ['options'], message: 'Option views must match the workspace manifest' });
});
export const projectCreateRequestSchema = strict({
  projectId: opaqueIdSchema.optional(), mutationId: opaqueIdSchema, document: projectManifestSchema,
});
export const projectSaveRequestSchema = strict({
  expectedRevision: revisionSchema, mutationId: opaqueIdSchema, document: projectManifestSchema,
});
export const projectOptionRequestSchema = strict({
  expectedRevision: revisionSchema, mutationId: opaqueIdSchema, document: projectManifestSchema,
  newDesign: solarDesignSchema.optional(),
});
export const designDocumentSnapshotSchema = strict({ projectId: opaqueIdSchema, documentType: z.literal('solar.design'), revision: revisionSchema, document: solarDesignSchema, savedAt: timestamp });

export const workspaceFileSchema = strict({
  attachmentId: entityIdSchema, assetId: entityIdSchema, filename: z.string().min(1).max(500),
  title: z.string().max(200).nullable(), mimeType: z.enum(['application/pdf', 'image/jpeg', 'image/png']),
  sizeBytes: z.number().int().nonnegative(), uploadedAt: timestamp,
});
export const workspaceFileLimitsSchema = strict({
  maxFileBytes: z.number().int().positive(), maxFiles: z.number().int().positive(),
  allowedMimeTypes: z.array(z.enum(['application/pdf', 'image/jpeg', 'image/png'])).min(1).max(3),
}).superRefine((value, ctx) => {
  if (new Set(value.allowedMimeTypes).size !== value.allowedMimeTypes.length)
    ctx.addIssue({ code: 'custom', path: ['allowedMimeTypes'], message: 'Duplicate MIME type' });
});
export const workspaceFilePageSchema = strict({
  apiVersion: z.literal(2), projectId: opaqueIdSchema, items: z.array(workspaceFileSchema),
  total: z.number().int().nonnegative(), page: z.number().int().nonnegative(), size: z.number().int().min(1).max(96),
  limits: workspaceFileLimitsSchema,
}).superRefine((value, ctx) => {
  const expected = Math.min(value.size, Math.max(0, value.total - value.page * value.size));
  if (value.items.length !== expected) ctx.addIssue({ code: 'custom', path: ['items'], message: 'File page does not match its filtered total' });
  if (value.total > value.limits.maxFiles) ctx.addIssue({ code: 'custom', path: ['total'], message: 'File count exceeds the server limit' });
  value.items.forEach((file, index) => {
    if (file.sizeBytes > value.limits.maxFileBytes || !value.limits.allowedMimeTypes.includes(file.mimeType))
      ctx.addIssue({ code: 'custom', path: ['items', index], message: 'File metadata exceeds the server limits' });
  });
  if (new Set(value.items.map(file => file.attachmentId)).size !== value.items.length || new Set(value.items.map(file => file.assetId)).size !== value.items.length)
    ctx.addIssue({ code: 'custom', path: ['items'], message: 'Duplicate file identity' });
});
export const workspaceFileUploadResponseSchema = strict({ apiVersion: z.literal(2), projectId: opaqueIdSchema, file: workspaceFileSchema });
export const workspaceHistoryItemSchema = strict({ revision: revisionSchema, savedAt: timestamp, restoredFromRevision: revisionSchema.nullable() });
export const workspaceHistoryPageSchema = strict({
  apiVersion: z.literal(2), projectId: opaqueIdSchema, designId: opaqueIdSchema.nullable(),
  items: z.array(workspaceHistoryItemSchema), total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(), size: z.number().int().min(1).max(96),
}).superRefine((value, ctx) => {
  const expected = Math.min(value.size, Math.max(0, value.total - value.page * value.size));
  if (value.items.length !== expected) ctx.addIssue({ code: 'custom', path: ['items'], message: 'History page does not match its filtered total' });
  if (new Set(value.items.map(item => item.revision)).size !== value.items.length)
    ctx.addIssue({ code: 'custom', path: ['items'], message: 'Duplicate history revision' });
  for (let index = 1; index < value.items.length; index++) {
    if (BigInt(value.items[index - 1]!.revision) <= BigInt(value.items[index]!.revision)) {
      ctx.addIssue({ code: 'custom', path: ['items', index], message: 'History revisions must be newest first' });
      break;
    }
  }
});

export const requestLineSchema = strict({
  lineId: opaqueIdSchema, component: componentReferenceSchema, description: z.string().trim().min(1).max(1000),
  quantity: quantitySchema, uom: z.string().min(1),
});

/** Ownership, stock, totals, fingerprints and quote status are server-derived and absent here. */
export const projectRequestInputSchema = strict({
  apiVersion: z.literal(2), mutationId: opaqueIdSchema,
  kind: z.enum(['EQUIPMENT', 'DESIGN_ASSISTANCE']), title: z.string().trim().min(1).max(200),
  message: z.string().max(4000), designRef: designReferenceSchema.nullable(),
  lines: z.array(requestLineSchema).max(50), attachmentIds: z.array(entityIdSchema),
  phone: z.string().max(40).optional(),
  delivery: strict({ postcode: z.string().max(20), notes: z.string().max(2000),
    requiredBy: z.string().date().nullable(), purchaseOrder: z.string().max(100).nullable() }).optional(),
}).superRefine((value, ctx) => {
  if (value.kind === 'EQUIPMENT' && value.lines.length === 0) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Equipment requests need at least one line' });
  if (value.kind === 'DESIGN_ASSISTANCE' && !value.message.trim()) ctx.addIssue({ code: 'custom', path: ['message'], message: 'Describe the assistance needed' });
  if (new Set(value.lines.map((line) => line.lineId)).size !== value.lines.length) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Duplicate request line IDs' });
  if (new Set(value.attachmentIds).size !== value.attachmentIds.length) ctx.addIssue({ code: 'custom', path: ['attachmentIds'], message: 'Duplicate attachments' });
});

/** Shared Enquiry path; old requests without requestMode retain their 50-line contract. */
export const sharedProjectRequestInputSchema = strict({
  apiVersion: z.literal(2), requestMode: z.literal('SHARED'), mutationId: opaqueIdSchema,
  kind: z.enum(['EQUIPMENT', 'DESIGN_ASSISTANCE']), title: z.string().trim().min(1).max(200),
  message: z.string().max(4000), designRef: designReferenceSchema.nullable(),
  lines: z.array(requestLineSchema).max(500), attachmentIds: z.array(z.number().int().positive().refine(Number.isSafeInteger)),
  contact: strict({ name: z.string().trim().min(1).max(200), email: z.string().email().max(200), phone: z.string().max(40).optional() }),
  delivery: strict({ postcode: z.string().max(20).optional(), notes: z.string().max(2000).optional(),
    requiredBy: z.string().date().nullable().optional(), purchaseOrder: z.string().max(100).optional() }).optional(),
}).superRefine((value, ctx) => {
  if (value.kind === 'EQUIPMENT' && value.lines.length === 0) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Equipment requests need at least one line' });
  if (value.kind === 'DESIGN_ASSISTANCE' && !value.message.trim()) ctx.addIssue({ code: 'custom', path: ['message'], message: 'Describe the assistance needed' });
  if (new Set(value.lines.map((line) => line.lineId)).size !== value.lines.length) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Duplicate request line IDs' });
  if (new Set(value.attachmentIds).size !== value.attachmentIds.length) ctx.addIssue({ code: 'custom', path: ['attachmentIds'], message: 'Duplicate attachments' });
  const deliveryNotes = value.delivery?.notes?.trim() ?? '';
  const message = value.message.trim();
  if (message.length + deliveryNotes.length + (deliveryNotes ? message ? 12 : 10 : 0) > 4000)
    ctx.addIssue({ code: 'custom', path: ['delivery', 'notes'], message: 'Message and delivery notes exceed 4000 characters combined' });
});

export const geographicPointSchema = strict({ latitude: finite.min(-90).max(90), longitude: finite.min(-180).max(180) });
export const planePointSchema = strict({ xMm: finite, yMm: finite });
const ring = z.array(planePointSchema).min(3).refine((points) => new Set(points.map((p) => `${p.xMm},${p.yMm}`)).size === points.length, 'Use distinct vertices with implicit closure');
const bearing = finite.min(0).lt(360);

export const geometryProvenanceSchema = z.discriminatedUnion('kind', [
  strict({ kind: z.literal('USER_MEASURED'), recordedAt: timestamp, reference: z.string().min(1) }),
  strict({ kind: z.literal('USER_ENTERED'), recordedAt: timestamp }),
  strict({ kind: z.literal('GOOGLE_SOLAR'), observationId: opaqueIdSchema, observedAt: timestamp, expiresAt: timestamp }),
  strict({ kind: z.literal('LEGACY_UNCONFIRMED'), designId: opaqueIdSchema, revision: revisionSchema }),
]);

/** Geometry primitives only. Topology, projections and engineering checks are separate DP.5 work. */
export const roofFaceContractSchema = strict({
  contractVersion: z.literal(2), roofId: opaqueIdSchema, includedInInstallation: z.boolean(),
  geographicOutline: z.array(geographicPointSchema).min(3).nullable(),
  plane: strict({ outer: ring, holes: z.array(ring),
    frame: strict({ version: z.literal(1), origin: geographicPointSchema.nullable(), eavesDirectionBearingDeg: bearing.nullable() }) }),
  pitchDeg: finite.min(0).lt(90).nullable(), bearingDeg: bearing.nullable(),
  gutterEdgeIndex: z.number().int().nonnegative().nullable(),
  verification: z.enum(['AWAITING_SURVEY', 'USER_CONFIRMED']), provenance: geometryProvenanceSchema,
}).superRefine((value, ctx) => {
  if (value.gutterEdgeIndex !== null && value.gutterEdgeIndex >= value.plane.outer.length) ctx.addIssue({ code: 'custom', path: ['gutterEdgeIndex'], message: 'Unknown gutter edge' });
  if (value.verification === 'USER_CONFIRMED' && (value.pitchDeg === null || value.bearingDeg === null || value.provenance.kind === 'LEGACY_UNCONFIRMED')) {
    ctx.addIssue({ code: 'custom', path: ['verification'], message: 'Confirm orientation and pitch before marking the face confirmed' });
  }
  if (value.provenance.kind === 'GOOGLE_SOLAR') {
    const retentionMs = Date.parse(value.provenance.expiresAt) - Date.parse(value.provenance.observedAt);
    if (retentionMs <= 0 || retentionMs > 30 * 24 * 60 * 60 * 1000) {
      ctx.addIssue({ code: 'custom', path: ['provenance'], message: 'Google observations must expire within 30 days of the original fetch' });
    }
  }
});

export type CataloguePageV2 = z.infer<typeof cataloguePageV2Schema>;
export type ProjectManifest = z.infer<typeof projectManifestSchema>;
export type ProjectPage = z.infer<typeof projectPageSchema>;
export type ProjectView = z.infer<typeof projectViewSchema>;
export type ProjectCreateRequest = z.infer<typeof projectCreateRequestSchema>;
export type ProjectSaveRequest = z.infer<typeof projectSaveRequestSchema>;
export type ProjectOptionRequest = z.infer<typeof projectOptionRequestSchema>;
export type DesignDocumentSnapshot = z.infer<typeof designDocumentSnapshotSchema>;
export type WorkspaceFile = z.infer<typeof workspaceFileSchema>;
export type WorkspaceFilePage = z.infer<typeof workspaceFilePageSchema>;
export type WorkspaceFileUploadResponse = z.infer<typeof workspaceFileUploadResponseSchema>;
export type WorkspaceHistoryPage = z.infer<typeof workspaceHistoryPageSchema>;
export type ProjectRequestInput = z.infer<typeof projectRequestInputSchema>;
export type SharedProjectRequestInput = z.infer<typeof sharedProjectRequestInputSchema>;
/** Actor-scoped project associations returned by the request directory. */
export const projectRequestPageSchema = strict({
  apiVersion: z.literal(2), projectId: opaqueIdSchema,
  page: z.number().int().nonnegative(), size: z.number().int().min(1).max(100), total: z.number().int().nonnegative(),
  items: z.array(strict({
    requestId: entityIdSchema, kind: z.enum(['EQUIPMENT', 'DESIGN_ASSISTANCE']),
    title: z.string().max(200).nullable(), state: z.string().min(1).max(80),
    optionId: opaqueIdSchema.nullable().optional(), designId: opaqueIdSchema.nullable().optional(),
    designRevision: revisionSchema.nullable().optional(), submittedAt: timestamp.nullable().optional(),
  })).max(100),
}).superRefine((value, ctx) => {
  if (value.items.length > value.size || value.items.length > value.total || new Set(value.items.map((item) => item.requestId)).size !== value.items.length)
    ctx.addIssue({ code: 'custom', path: ['items'], message: 'Invalid request page' });
});
export type ProjectRequestPage = z.infer<typeof projectRequestPageSchema>;
const publicMoney = z.string().regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/).max(64).nullable();
const issuedOfferLineSchema = strict({
  id: z.string().min(1).max(100), description: z.string().max(500).nullable(), quantity: publicMoney,
  unitPrice: publicMoney, taxAmount: publicMoney, lineTotal: publicMoney,
});
const issuedOfferSchema = strict({
  id: z.string().min(1).max(100), number: z.string().max(100).nullable(), revision: z.string().min(1).max(100),
  status: z.string().min(1).max(80), currency: z.string().regex(/^[A-Z]{3}$/).nullable(),
  subtotal: publicMoney, taxTotal: publicMoney, total: publicMoney,
  sentAt: timestamp.nullable(), expiresAt: timestamp.nullable(), lines: z.array(issuedOfferLineSchema).max(500),
});
/** Only server-authorised public facts accompany an already loaded exact saved design. */
export const customerPresentationSchema = strict({
  apiVersion: z.literal(2), projectId: opaqueIdSchema, designId: opaqueIdSchema,
  revision: revisionSchema, documentSha256: z.string().regex(/^[a-f0-9]{64}$/i),
  request: strict({ requestId: entityIdSchema, workspaceId: opaqueIdSchema, workspaceName: z.string().max(200), optionId: opaqueIdSchema.nullable() }).nullable(),
  latestPublicIssuedOffer: issuedOfferSchema.nullable(),
});
export type CustomerPresentationProjection = z.infer<typeof customerPresentationSchema>;
export type PinnedDesignReference = z.infer<typeof pinnedDesignReferenceSchema>;
export type RoofFaceContract = z.infer<typeof roofFaceContractSchema>;
